import {
  DiscountLifeCyclePolicyEnum,
  IQuoteItemRespSchema,
  IQuoteOfferingReqSchema,
} from '@monetize/types/app';
import { FC, useEffect } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  MButton,
  MCenterModal,
  MCheckbox,
  MFormField,
  MInput,
  MStack,
} from '../../../../../components/Monetize';
import { useCustomDiscountExcludedOnRenewal } from '../../hooks/useDiscountExcludedOnRenewal';

export interface QuoteItemDiscountEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  control: Control<IQuoteOfferingReqSchema>;
  errors: FieldErrors<IQuoteOfferingReqSchema>;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
  index: number;
  disabled?: boolean;
  itemFromServer: IQuoteItemRespSchema;
  handleQuoteOffering: () => void;
}

/**
 * Modal to allow editing the name and description for a custom discount
 */
export const QuoteItemDiscountEditModal: FC<
  QuoteItemDiscountEditModalProps
> = ({
  isOpen,
  onClose,
  control,
  setValue,
  errors,
  index,
  disabled,
  itemFromServer,
  handleQuoteOffering,
}: QuoteItemDiscountEditModalProps) => {
  const handleSave = () => {
    handleQuoteOffering();
    onClose();
  };

  const isCustomDiscountExcludedFromRenewal =
    useCustomDiscountExcludedOnRenewal(itemFromServer);

  useEffect(() => {
    if (isOpen) {
      setValue(
        `items.${index}.customDiscountName`,
        itemFromServer.customDiscountName,
      );

      setValue(
        `items.${index}.customDiscountDescription`,
        itemFromServer.customDiscountDescription,
      );

      setValue(
        `items.${index}.customDiscountExcludeFromRenewal`,
        isCustomDiscountExcludedFromRenewal,
      );
    }
  }, [
    isOpen,
    index,
    itemFromServer,
    setValue,
    isCustomDiscountExcludedFromRenewal,
  ]);

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Edit Discount"
      size="sm"
      renderFooter={({ onClose: modalOnClose }) => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            size="sm"
            variant="cancel"
            onClick={modalOnClose}
            isDisabled={disabled}
          >
            Cancel
          </MButton>
          <MButton
            size="sm"
            variant="primary"
            onClick={handleSave}
            isDisabled={disabled}
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      <MStack gap={4}>
        <MFormField
          error={errors?.items?.[index]?.customDiscountName}
          label="Discount Name"
        >
          <Controller
            name={`items.${index}.customDiscountName`}
            control={control}
            defaultValue=""
            render={({ field: { value, ...rest } }) => (
              <MInput
                value={value || ''}
                isDisabled={disabled}
                maxLength={45}
                placeholder="Enter discount name"
                {...rest}
              />
            )}
          />
        </MFormField>

        <MFormField
          error={errors?.items?.[index]?.customDiscountDescription}
          label="Description"
        >
          <Controller
            name={`items.${index}.customDiscountDescription`}
            control={control}
            defaultValue=""
            render={({ field: { value, ...rest } }) => (
              <MInput
                value={value || ''}
                isDisabled={disabled}
                placeholder="Add description to the custom discount"
                maxLength={100}
                {...rest}
              />
            )}
          />
        </MFormField>

        <MFormField
          error={errors?.items?.[index]?.customDiscountExcludeFromRenewal}
        >
          <Controller
            name={`items.${index}.customDiscountExcludeFromRenewal`}
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <MCheckbox
                isChecked={Boolean(value)}
                isDisabled={disabled}
                onChange={(e) => {
                  onChange(e.target.checked);
                  setValue(
                    `items.${index}.customDiscountLifecyclePolicies`,
                    e.target.checked
                      ? [DiscountLifeCyclePolicyEnum.EXCLUDE_FROM_RENEWAL]
                      : [],
                    { shouldDirty: true, shouldValidate: true },
                  );
                }}
                {...rest}
              >
                Remove Upon Renewal
              </MCheckbox>
            )}
          />
        </MFormField>
      </MStack>
    </MCenterModal>
  );
};
